import { MULTI_SELECT_TEMPORARY_ID } from '@/editor/constant/entity';
import { IMultiModelData } from '@/editor/data/multiModel/multiModel';
import { adsorbModelInfos, getModelInfoBox } from '@/editor/data/multiModel/utils';
import { OperateSelectedBoxes } from '@/editor/controller/helper/operateSelectedBoxes';
import { IPoint2d } from '@/editor/math/IPoint2d';
import { Point2dUtils } from '@/editor/math/Point2dUtils';
import { MultiModelAbsorbManager } from '@/editor/manager/absorb/box/multiModelAbsorbManager';
import { modelManager } from '@/editor/manager/entityManager';
import { TGlobalStores } from '@/editor/stores';
import { EEntityType, IRotatedBox } from '@/editor/types';
import { getMultiSelectedBoxByState } from '@/editor/utils/select';
import { cloneDeep } from 'lodash-es';
import { VirtualMultiModelEntities } from '@/editor/entities/virtualModelEntities';
import { OperateAbstract } from './operateAbstract';
import { isMultiSelectFurnitureOrAssemblyOnly } from '@/editor/utils/select/multi-select';

export class OperateMultiModel extends OperateAbstract<EEntityType.MultiModel> {
  private _multiModelAbsorbManager = new MultiModelAbsorbManager();
  private _selectedBoxes = new OperateSelectedBoxes();
  private _virtualEntities = new VirtualMultiModelEntities();

  private _checkedOffset: IPoint2d = { x: 0, y: 0 };
  private _originalMultiModelData: IMultiModelData | null = null;
  private _movedMultiModelData: IMultiModelData | null = null;

  constructor(_gs: TGlobalStores) {
    super(_gs);
  }

  override startMove(point: IPoint2d) {
    const multiModelData = this._gs.getDataByIdType('', EEntityType.MultiModel);
    if (!multiModelData) return false;
    if (!isMultiSelectFurnitureOrAssemblyOnly(multiModelData)) return false;

    this._originalMultiModelData = cloneDeep(multiModelData);
    this._movedMultiModelData = null;
    this.moving = false;

    const multiModelBoxData = getMultiSelectedBoxByState(
      this._originalMultiModelData.modelInfos.map((modelInfo) => {
        const box = getModelInfoBox(modelInfo);
        return { center: { x: box.x, y: box.y }, width: box.width, thickness: box.thickness, rotation: box.rotation };
      })
    );
    this._checkedOffset = Point2dUtils.subtracted(multiModelBoxData.center, point);

    this._virtualEntities.create(this._originalMultiModelData);
    this._multiModelAbsorbManager.updateData({ excludeModelIds: this._virtualEntities.furnitureIds });
    this._virtualEntities.setRealEntitiesVisible(false);

    this.updateBoxes();

    return true;
  }

  move(point: IPoint2d) {
    if (!this._originalMultiModelData) return;

    this.moving = true;

    const adsorbBoxData = this.getMultiModelAdsorbBoxData(
      Point2dUtils.added(point, this._checkedOffset),
      this._originalMultiModelData
    );

    this.updateMovedMultiModel(adsorbBoxData);
  }

  override moveByOffset(offset: IPoint2d) {
    if (!this._originalMultiModelData) return;

    this.moving = true;

    // 累积在移动后的数据上，但映射的源始终是原始数据，避免累积误差
    const base = this._movedMultiModelData ?? this._originalMultiModelData;
    this.updateMovedMultiModel({
      ...this._originalMultiModelData,
      center: Point2dUtils.added(base.center, offset),
      rotation: base.rotation
    });
  }

  private updateMovedMultiModel(boxData: IRotatedBox) {
    if (!this._originalMultiModelData) return;

    const modelInfos = adsorbModelInfos(this._originalMultiModelData.modelInfos, boxData, this._originalMultiModelData);

    this._movedMultiModelData = {
      ...this._originalMultiModelData,
      center: boxData.center,
      rotation: boxData.rotation,
      modelInfos
    };

    this._virtualEntities.update(this._movedMultiModelData);

    this.updateBoxes({
      x: boxData.center.x,
      y: boxData.center.y,
      rotation: boxData.rotation
    });
    modelManager.selectedHoveredManager.updateSelectedPositionAndRotation(boxData.center, boxData.rotation);
  }

  completeMove(_point: IPoint2d) {
    if (this._movedMultiModelData && this.moving) {
      this._gs.multiModelOperation.modifyMultiModel(this._movedMultiModelData.modelInfos);

      this._gs.editorStateStore.setSelected(
        {
          entityId: MULTI_SELECT_TEMPORARY_ID,
          type: EEntityType.MultiModel
        },
        { multiModelData: this._movedMultiModelData }
      );
    }

    this.clear();
  }

  clear() {
    this._multiModelAbsorbManager.clearModel();
    this._selectedBoxes.clear();
    this._virtualEntities.setRealEntitiesVisible(true);
    this._virtualEntities.dispose();

    this._checkedOffset = { x: 0, y: 0 };
    this.checkedEntity = null;
    this._originalMultiModelData = null;
    this._movedMultiModelData = null;
    this.moving = false;
  }

  dispose() {
    this.clear();
    this._multiModelAbsorbManager.dispose();
    this._selectedBoxes.dispose();
  }

  private getMultiModelAdsorbBoxData(center: IPoint2d, originBoxData: IRotatedBox) {
    const boxData = cloneDeep(originBoxData);
    boxData.center = center;

    const absorbResult = this._multiModelAbsorbManager.calculateThenRender(boxData);
    if (absorbResult) {
      boxData.center = absorbResult.absorbPoint;
      boxData.rotation = absorbResult.rotation;
    }

    return boxData;
  }

  private updateBoxes(params?: { x: number; y: number; rotation: number }) {
    if (!this._originalMultiModelData) return;

    this._selectedBoxes.update({ type: EEntityType.MultiModel, entityId: MULTI_SELECT_TEMPORARY_ID }, params);
  }
}
